import { Web3Provider, JsonRpcSigner } from '@ethersproject/providers'
import { BASE_CHAIN_ID_NUMBER, ETH_CHAIN_ID_NUMBER } from '@mprolab/sdk'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { NodeOwners } from 'config/abi/types'
import { getProviderOrSigner } from 'utils'
import { getNodeOwnersContract } from 'utils/contractHelpers'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import useActiveWeb3React from './useActiveWeb3React'

export enum ContractType {
  NODE_OWNERS = 'node_owners',
}

export const useWeb3ProviderAndContract = (): {
  getProviderOrSignerByChainId: (chainId: number, account?: string) => Web3Provider | JsonRpcSigner
  getContractByChainId: ({
    contractAddress,
    chainId,
    contractType,
    account,
  }: {
    contractAddress: string
    chainId: number
    contractType: ContractType
    account?: string
  }) => NodeOwners | null
} => {
  const { provider: web3ReactProvider } = useWeb3React()
  const { provider: ethProvider } = useActiveWeb3React(ETH_CHAIN_ID_NUMBER)
  const { provider: baseProvider } = useActiveWeb3React(BASE_CHAIN_ID_NUMBER)

  const getProviderOrSignerByChainId = (chainId: number, account?: string): Web3Provider | JsonRpcSigner => {
    /**
     * Signing goes to the WALLET, always. Never to a chain-selected provider.
     *
     * The per-chain providers fall back to a read-only RPC node whenever the
     * wallet is not already on that chain in the current render — and a public
     * node holds nobody's keys, so `eth_sendTransaction` came back
     * `-32602 unknown account` against our own /api/rpc/base. It looked like a
     * flaky node and was not: the app was asking a stranger to sign.
     *
     * Callers switch the chain with `useSwitchChainForContractTx` before they
     * get here, and the wallet sends on whatever chain it is actually on — so
     * the wallet's own view is both the only one that can sign and the only
     * one that is never stale.
     */
    if (account) {
      return getProviderOrSigner(web3ReactProvider as Web3Provider, account)
    }

    switch (chainId) {
      case ETH_CHAIN_ID_NUMBER:
        return ethProvider as Web3Provider
      case BASE_CHAIN_ID_NUMBER:
        return baseProvider as Web3Provider
      default:
        return web3ReactProvider as Web3Provider
    }
  }

  const getContractByChainId = ({
    contractAddress,
    chainId,
    contractType,
    account,
  }: {
    contractAddress: string
    chainId: number
    contractType: ContractType
    account?: string
  }): NodeOwners | null => {
    switch (contractType) {
      case ContractType.NODE_OWNERS:
        return getNodeOwnersContract(
          contractAddress,
          account ? getProviderOrSignerByChainId(chainId, account) : getSimpleRpcProviderByChainId(chainId),
        )
      default:
        return null
    }
  }

  return { getProviderOrSignerByChainId, getContractByChainId }
}
